/**
 * Hook to query LOC events from the blockchain
 * Since the contract doesn't have getUserLOCs(), we query LOCCreatedV2 events
 */

import { useState, useEffect } from 'react'
import { usePublicClient } from 'wagmi'
import { getContractAddress } from '@contracts/addresses'
import { Address } from '../types/contracts'

export interface LOCEvent {
  id: bigint
  collateralId: bigint
  creator: Address
  beneficiary: Address
  tag: string
  collateralTokenAddress: Address
  collateralTokenAmount: bigint
  claimableCollateral: bigint
  expirationTimestamp: number
  collateralFactorBasisPoints: number
  liquidatorIncentiveBasisPoints: number
  creditedTokenAddress: Address
  creditedTokenAmount: bigint
  blockNumber: bigint
  transactionHash: string
}

// ─── LocalStorage Cache — P2 task #4.11 ─────────────────────────────────────

const CACHE_KEY_PREFIX = 'loc_events_'
const CACHE_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes

interface CachedLOCData {
  locs: LOCEvent[]
  timestamp: number
}

function getCachedLOCs(userAddress: Address): LOCEvent[] | null {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${userAddress.toLowerCase()}`)
    if (!cached) return null

    const data: CachedLOCData = JSON.parse(cached)
    const age = Date.now() - data.timestamp

    if (age > CACHE_EXPIRY_MS) {
      // Expired cache
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${userAddress.toLowerCase()}`)
      return null
    }

    // Convert bigint strings back to bigint
    return data.locs.map(loc => ({
      ...loc,
      id: BigInt(loc.id),
      collateralId: BigInt(loc.collateralId),
      collateralTokenAmount: BigInt(loc.collateralTokenAmount),
      claimableCollateral: BigInt(loc.claimableCollateral),
      creditedTokenAmount: BigInt(loc.creditedTokenAmount),
      blockNumber: BigInt(loc.blockNumber),
    }))
  } catch (err) {
    console.error('[useLOCEvents] Failed to read cache:', err)
    return null
  }
}

function setCachedLOCs(userAddress: Address, locs: LOCEvent[]) {
  try {
    // Convert bigints to strings for JSON serialization
    const serializable = locs.map(loc => ({
      ...loc,
      id: loc.id.toString(),
      collateralId: loc.collateralId.toString(),
      collateralTokenAmount: loc.collateralTokenAmount.toString(),
      claimableCollateral: loc.claimableCollateral.toString(),
      creditedTokenAmount: loc.creditedTokenAmount.toString(),
      blockNumber: loc.blockNumber.toString(),
    }))

    const data: CachedLOCData = {
      locs: serializable as any,
      timestamp: Date.now(),
    }

    localStorage.setItem(`${CACHE_KEY_PREFIX}${userAddress.toLowerCase()}`, JSON.stringify(data))
  } catch (err) {
    console.error('[useLOCEvents] Failed to write cache:', err)
  }
}

export function clearLOCCache(userAddress?: Address) {
  if (userAddress) {
    localStorage.removeItem(`${CACHE_KEY_PREFIX}${userAddress.toLowerCase()}`)
  } else {
    // Clear all LOC caches
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
  }
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to fetch user's LOCs by querying LOCCreatedV2 events
 * Features localStorage caching (5 min TTL)
 */
export function useUserLOCsFromEvents(userAddress?: Address) {
  const [locs, setLocs] = useState<LOCEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const publicClient = usePublicClient()

  const fetchLOCs = async (skipCache = false) => {
    if (!userAddress || !publicClient) {
      setLocs([])
      return
    }

    // Check cache first (unless explicitly skipped)
    if (!skipCache) {
      const cached = getCachedLOCs(userAddress)
      if (cached) {
        console.log('📦 [useLOCEvents] Using cached LOCs:', cached.length)
        setLocs(cached)
        return
      }
    }

    setIsLoading(true)
    setError(null)

    try {
      const contractAddress = getContractAddress('LetterOfCreditProxy')
      
      // Get the current block number
      const currentBlock = await publicClient.getBlockNumber()
      
      // Query last 10,000 blocks (roughly 1.4 days of Ethereum blocks)
      const fromBlock = currentBlock - 100000n
      
      console.log('🔍 [useUserLOCsFromEvents] Querying LOCCreatedV2 events', {
        contractAddress,
        fromBlock: fromBlock.toString(),
        toBlock: currentBlock.toString(),
        creator: userAddress
      })

      // Query LOCCreatedV2 events where user is the creator
      const logs = await publicClient.getLogs({
        address: contractAddress,
        event: {
          type: 'event',
          name: 'LOCCreatedV2',
          inputs: [
            { type: 'address', indexed: true, name: 'creator' },
            { type: 'address', indexed: true, name: 'beneficiary' },
            { type: 'bytes32', indexed: true, name: 'tag' },
            { type: 'address', indexed: false, name: 'collateralContractAddress' },
            { type: 'address', indexed: false, name: 'collateralTokenAddress' },
            { type: 'uint256', indexed: false, name: 'collateralTokenAmount' },
            { type: 'uint256', indexed: false, name: 'claimableCollateral' },
            { type: 'uint32', indexed: false, name: 'expirationTimestamp' },
            { type: 'uint16', indexed: false, name: 'collateralFactorBasisPoints' },
            { type: 'uint16', indexed: false, name: 'liquidatorIncentiveBasisPoints' },
            { type: 'address', indexed: false, name: 'creditedTokenAddress' },
            { type: 'uint256', indexed: false, name: 'creditedTokenAmount' },
            { type: 'uint96', indexed: false, name: 'id' },
            { type: 'uint96', indexed: false, name: 'collateralId' },
          ],
        },
        args: {
          creator: userAddress,
        },
        fromBlock,
        toBlock: currentBlock,
      })

      console.log(`📋 [useUserLOCsFromEvents] Found ${logs.length} LOCs`)

      // Parse the events
      const parsedLOCs: LOCEvent[] = logs.map((log) => {
        const args = log.args as any
        return {
          id: args.id,
          collateralId: args.collateralId,
          creator: args.creator,
          beneficiary: args.beneficiary,
          tag: args.tag || '0x0000000000000000000000000000000000000000000000000000000000000000',
          collateralTokenAddress: args.collateralTokenAddress,
          collateralTokenAmount: args.collateralTokenAmount,
          claimableCollateral: args.claimableCollateral,
          expirationTimestamp: args.expirationTimestamp,
          collateralFactorBasisPoints: args.collateralFactorBasisPoints,
          liquidatorIncentiveBasisPoints: args.liquidatorIncentiveBasisPoints,
          creditedTokenAddress: args.creditedTokenAddress,
          creditedTokenAmount: args.creditedTokenAmount,
          blockNumber: log.blockNumber,
          transactionHash: log.transactionHash,
        }
      })

      // Sort by most recent first
      parsedLOCs.sort((a, b) => Number(b.blockNumber - a.blockNumber))

      // Save to cache
      setCachedLOCs(userAddress, parsedLOCs)

      setLocs(parsedLOCs)
    } catch (err) {
      console.error('❌ [useUserLOCsFromEvents] Error fetching LOC events:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch LOC events'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchLOCs()
  }, [userAddress, publicClient])

  return {
    locs,
    isLoading,
    error,
    refetch: fetchLOCs,
  }
}

/**
 * Hook to fetch LOCs where user is beneficiary
 */
export function useBeneficiaryLOCsFromEvents(beneficiaryAddress?: Address) {
  const [locs, setLocs] = useState<LOCEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const publicClient = usePublicClient()

  const fetchLOCs = async () => {
    if (!beneficiaryAddress || !publicClient) {
      setLocs([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const contractAddress = getContractAddress('LetterOfCreditProxy')
      const currentBlock = await publicClient.getBlockNumber()
      const fromBlock = currentBlock - 100000n

      console.log('🔍 [useBeneficiaryLOCsFromEvents] Querying LOCCreatedV2 events', {
        contractAddress,
        fromBlock: fromBlock.toString(),
        toBlock: currentBlock.toString(),
        beneficiary: beneficiaryAddress
      })

      // Query LOCCreatedV2 events where user is the beneficiary
      const logs = await publicClient.getLogs({
        address: contractAddress,
        event: {
          type: 'event',
          name: 'LOCCreatedV2',
          inputs: [
            { type: 'address', indexed: true, name: 'creator' },
            { type: 'address', indexed: true, name: 'beneficiary' },
            { type: 'bytes32', indexed: true, name: 'tag' },
            { type: 'address', indexed: false, name: 'collateralContractAddress' },
            { type: 'address', indexed: false, name: 'collateralTokenAddress' },
            { type: 'uint256', indexed: false, name: 'collateralTokenAmount' },
            { type: 'uint256', indexed: false, name: 'claimableCollateral' },
            { type: 'uint32', indexed: false, name: 'expirationTimestamp' },
            { type: 'uint16', indexed: false, name: 'collateralFactorBasisPoints' },
            { type: 'uint16', indexed: false, name: 'liquidatorIncentiveBasisPoints' },
            { type: 'address', indexed: false, name: 'creditedTokenAddress' },
            { type: 'uint256', indexed: false, name: 'creditedTokenAmount' },
            { type: 'uint96', indexed: false, name: 'id' },
            { type: 'uint96', indexed: false, name: 'collateralId' },
          ],
        },
        args: {
          beneficiary: beneficiaryAddress,
        },
        fromBlock,
        toBlock: currentBlock,
      })

      console.log(`📋 [useBeneficiaryLOCsFromEvents] Found ${logs.length} LOCs`)

      const parsedLOCs: LOCEvent[] = logs.map((log) => {
        const args = log.args as any
        return {
          id: args.id,
          collateralId: args.collateralId,
          creator: args.creator,
          beneficiary: args.beneficiary,
          tag: args.tag || '0x0000000000000000000000000000000000000000000000000000000000000000',
          collateralTokenAddress: args.collateralTokenAddress,
          collateralTokenAmount: args.collateralTokenAmount,
          claimableCollateral: args.claimableCollateral,
          expirationTimestamp: args.expirationTimestamp,
          collateralFactorBasisPoints: args.collateralFactorBasisPoints,
          liquidatorIncentiveBasisPoints: args.liquidatorIncentiveBasisPoints,
          creditedTokenAddress: args.creditedTokenAddress,
          creditedTokenAmount: args.creditedTokenAmount,
          blockNumber: log.blockNumber,
          transactionHash: log.transactionHash,
        }
      })

      parsedLOCs.sort((a, b) => Number(b.blockNumber - a.blockNumber))

      setLocs(parsedLOCs)
    } catch (err) {
      console.error('❌ [useBeneficiaryLOCsFromEvents] Error fetching LOC events:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch LOC events'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchLOCs()
  }, [beneficiaryAddress, publicClient])

  return {
    locs,
    isLoading,
    error,
    refetch: fetchLOCs,
  }
}

